import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PostcardFace } from "@/components/PostcardFace";
import { Footer } from "@/components/Footer";
import { LOGO_IMG, POSTCARD_IMG } from "@/lib/brand";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowsClockwise, MapPin } from "@phosphor-icons/react";

export default function SponsorView() {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [slots, setSlots] = useState([]);
  const [face, setFace] = useState("Front");
  const [leadSlot, setLeadSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", message: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/campaigns/${campaignId}`).then(({ data }) => setCampaign(data)).catch(() => {});
    api.get(`/campaigns/${campaignId}/slots`).then(({ data }) => setSlots(data));
  }, [campaignId]);

  const openLead = (slot) => { setLeadSlot(slot); setForm({ name: "", email: "", phone: "", business_name: "", message: "" }); };

  const submitLead = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/leads", { ...form, campaign_id: campaignId, slot_id: leadSlot?.id });
      toast.success("Thanks! Our team will reach out to reserve your spot.");
      setLeadSlot(null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const available = slots.filter((s) => s.status === "Available").length;
  const cardImg = (face === "Back" ? campaign?.back_image_id : campaign?.front_image_id) ? fileUrl(face === "Back" ? campaign.back_image_id : campaign.front_image_id) : POSTCARD_IMG;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><img src={LOGO_IMG} alt="PMI" className="w-40 sm:w-[220px] h-auto" /></Link>
          {campaign && <span className="hidden sm:flex items-center gap-2 text-sm text-slate-500"><MapPin size={16} className="text-amber-600" /> {campaign.label}</span>}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 pt-12 pb-4">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Advertiser Preview · {campaign?.status === "published" ? "Live" : "In Progress"}</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-2">Claim your spot on this drop</h1>
        <p className="text-slate-500 mt-3 max-w-xl">{available} of 16 slots still available. See real-time availability over the printed 9×12 mailer and book your spot instantly.</p>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-6">
        <div className="grid md:grid-cols-2 gap-6 items-center bg-white border border-slate-200 p-4 shadow-sm">
          <img src={cardImg} alt={`Mailer ${face}`} className="w-full object-contain rounded-sm border border-slate-100" />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Live Availability · {face} Face</p>
            <h2 className="font-display text-2xl tracking-tight mt-1">Map your ad to the physical card</h2>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">Green tiles are open. Click any open tile below to book a spot; amber tiles are already reserved.</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-4">
        <div className="inline-flex border border-slate-300 p-1 bg-white" data-testid="sponsor-face-toggle">
          {["Front", "Back"].map((f) => (
            <button key={f} onClick={() => setFace(f)} data-testid={`sponsor-face-${f}`}
              className={`px-6 py-2 text-sm font-[600] flex items-center gap-2 transition-colors ${face === f ? "bg-navy-900 text-white" : "text-slate-500"}`}>
              <ArrowsClockwise size={14} /> {f} Face
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-24">
        <div className="bg-white border-r border-b border-slate-300 shadow-xl">
          <PostcardFace face={face} slots={slots} mode="sponsor" selected={[]} onSlotClick={openLead} />
        </div>
      </div>

      <Dialog open={!!leadSlot} onOpenChange={(o) => !o && setLeadSlot(null)}>
        <DialogContent data-testid="lead-form">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Claim {leadSlot?.face} · Slot {leadSlot?.slot_number}</DialogTitle>
            <DialogDescription>Book this spot on {campaign?.label}. Our team confirms within one business day.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitLead} className="space-y-3">
            <input required placeholder="Business name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="lead-business" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <div className="grid grid-cols-2 gap-3">
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email" className="border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
              <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone" className="border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            </div>
            <textarea placeholder="Anything you'd like us to know?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2} className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <button type="submit" disabled={saving} data-testid="lead-submit" className="w-full bg-navy-900 text-white py-3 font-[600] hover:bg-emerald-700 transition-colors disabled:opacity-60">
              {saving ? "Booking…" : "Book This Spot"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
